/**
 * Account health presentation. §7.3 requires that when health is not `healthy`
 * the header names the state *and the required action* — "degraded" alone tells
 * an owner nothing they can act on.
 */

import type { RuntimeState, SocialAccount, SocialAccountStatus } from '../api/types';
import { Badge, type Tone } from './ui';
import { localDateTime } from '../lib/format';

interface HealthCopy {
  tone: Tone;
  state: string;
  action: string;
}

export function healthCopy(status: SocialAccountStatus): HealthCopy {
  switch (status) {
    case 'healthy':
      return { tone: 'ok', state: 'Healthy', action: '' };
    case 'needs_reauth':
      return {
        tone: 'warn',
        state: 'Needs re-authorization',
        // Routine under the browser transport rather than exceptional: sessions
        // expire, and how fast an owner clears this is a listing quality signal.
        action: 'Sign in again to reconnect this account. Nothing sends until you do.',
      };
    case 'challenged':
      return {
        tone: 'warn',
        state: 'Verification requested by the platform',
        action:
          'The platform is asking this account to verify itself. Sign in there and clear the prompt, then reconnect here.',
      };
    case 'locked':
      return {
        tone: 'bad',
        state: 'Locked by the platform',
        action:
          'The platform has locked this account. Only the account holder can unlock it, on the platform itself. Rentals are blocked until it is.',
      };
    case 'banned':
      return {
        tone: 'bad',
        state: 'Permanently suspended by the platform',
        // Saying "reconnect" here would be a lie. This state usually does not
        // recover, and the owner needs to know that rather than retry.
        action:
          'This account is gone and will not come back by reconnecting. Detach it and connect another to keep the bot rentable.',
      };
    case 'rate_limited':
      return {
        tone: 'warn',
        state: 'Rate limited',
        action: 'Wait for the quota to reset; queued items will send after that.',
      };
    case 'suspended':
      return {
        tone: 'bad',
        state: 'Suspended by the platform',
        action: 'Resolve the suspension with the social platform. Nothing can be sent until then.',
      };
    case 'revoked':
      return {
        tone: 'bad',
        state: 'Access revoked',
        action: 'Reconnect the account to restore access.',
      };
  }
}

export function AccountHealthBadge({ status }: { status: SocialAccountStatus }) {
  const c = healthCopy(status);
  return <Badge tone={c.tone}>{c.state}</Badge>;
}

export function AccountHealthLine({ account }: { account: SocialAccount }) {
  const c = healthCopy(account.status);
  return (
    <div className="health-line">
      <AccountHealthBadge status={account.status} />
      <span className="muted">
        {account.platform} · @{account.external_handle}
      </span>
      {c.action ? <span className="health-action">{c.action}</span> : null}
      {account.status === 'rate_limited' && account.rate_limit_reset_at ? (
        <span className="muted">Resets {localDateTime(account.rate_limit_reset_at)}.</span>
      ) : null}
      {account.detail ? <span className="muted">{account.detail}</span> : null}
    </div>
  );
}

/** Worst status across a bot's accounts drives the persistent header warning. */
const SEVERITY: Record<SocialAccountStatus, number> = {
  healthy: 0,
  rate_limited: 1,
  // Both are cleared by the owner and both stop dispatch, but a challenge is a
  // prompt to answer where a stale session is just a sign-in.
  needs_reauth: 2,
  challenged: 3,
  revoked: 4,
  // Locked and suspended are platform-side and need action off this platform.
  locked: 5,
  suspended: 6,
  // Terminal. Nothing the owner does here recovers it.
  banned: 7,
};

export function worstAccount(accounts: SocialAccount[]): SocialAccount | null {
  if (accounts.length === 0) return null;
  const sorted = [...accounts].sort((a, b) => SEVERITY[b.status] - SEVERITY[a.status]);
  const first = sorted[0]!;
  return first.status === 'healthy' ? null : first;
}

const RUNTIME_TONE: Record<RuntimeState, Tone> = {
  running: 'ok',
  paused: 'warn',
  stopped: 'neutral',
};

/** Always visible in the Console header, never behind a menu (§7.3). */
export function RuntimeBadge({ state }: { state: RuntimeState }) {
  return (
    <Badge tone={RUNTIME_TONE[state]}>
      <span className={`dot dot-${state}`} aria-hidden="true" /> {state}
    </Badge>
  );
}
